import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { Subscription, mergeMap, catchError, of } from 'rxjs';
import { ICustomerRss } from 'src/app/shared/models/ICustomer-rss';
import { IFacility } from 'src/app/shared/models/IFacility';
import { IFacilityRss } from 'src/app/shared/models/IFacility-rss';
import { IIDN } from 'src/app/shared/models/IIDN';
import { ICountry, IState } from 'src/app/shared/models/lookup.model';
import { ICulture } from 'src/app/shared/models/resource.model';
import { CultureService } from 'src/app/shared/services/culture.service';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { ContainsSpaceValidator } from 'src/app/shared/util/containsSpace.validator';
import { OnlySpacesValidator } from 'src/app/shared/util/onlySpaces.validator';
import { IdnService } from '../../idns/shared/idn.service';
import { FacilitiesService } from '../shared/facilities.service';
import { FacilityValidator } from '../shared/facility.validator';

@Component({
  selector: 'app-add-facility',
  templateUrl: './add-facility.component.html',
  styleUrls: ['./add-facility.component.css']
})
export class AddFacilityComponent implements OnInit, OnDestroy {

  resources: any;
  allIDNs: IIDN[];
  allIDNsCustomersFromRSS: ICustomerRss[];
  allHospitals: IFacility[];
  allCustomerFacilities: IFacilityRss[];
  allCountries: ICountry[];
  allStates: IState[];
  allLanguages: ICulture[];
  resourceKeyValue: any;
  currentIdn: IIDN;
  currentOds: string;
  selectedCountryId: number = 0;
  idFacility = -1;
  selectedStateValue: string = '0';
  selectedIDNValue: number = 0;
  selectedCutomerValue: string = "0";
  customerRssId: string = "";
  selectedFacilityNameValue: string = "0";
  facilityRssId: string = "";
  selectedLanguageValue: number = 0;
  selectedIdnTenantCode: string = "";
  previousIdnTenantCode: string = "";
  resource: string = ""
  successMessageOnCreate = ""
  successMessageOnUpdate = ""
  errorMessageOnCreate = ""
  errorMessageOnUpdate = ""

  btnCreateText = "";
  lblCreateText = "";
  labelIdn = "";
  btnCancelText = "";
  isLoading = false;
  isEdit = false;
  isCreate = false;
  showComboBox = false;
  facilityServiceSubscription!: Subscription;
  zipPattern: any = "^[0-9]{5}-[0-9]{4}?$";
  purgePattern = "^([1-9][0-9]{0,2})$";

  showState: boolean = true;
  isDataLoaded: boolean = false;
  hospitalForm = this.fb.group({
    id: ['-1', []],
    customerName: ['', [Validators.required, Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    selectFacilityName: ['', [Validators.required, Validators.maxLength(200), OnlySpacesValidator.onlyWhiteSpace]],
    idn: ['', [Validators.required, OnlySpacesValidator.onlyWhiteSpace]],
    ods: ['', [Validators.required, Validators.maxLength(500), OnlySpacesValidator.onlyWhiteSpace]],
    accountNumber: ['', [Validators.maxLength(255), ContainsSpaceValidator.containsWhiteSpace]],
    address1: ['', [Validators.required, Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    address2: ['', [Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    city: [undefined, [Validators.required, Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    state: ['', [Validators.required, OnlySpacesValidator.onlyWhiteSpace]],
    zipCode: [undefined, [Validators.required, Validators.maxLength(10), Validators.pattern(this.zipPattern)]],
    countryId: ['', [Validators.required, OnlySpacesValidator.onlyWhiteSpace]],
    cultureId: ['', []],
    isCdsSubscribed: [true, []],
    purgeFrequency: ['', [Validators.required, Validators.pattern(this.purgePattern)]]
  });

  @ViewChild(ToastContainerDirective, { static: true })
  toastContainer: ToastContainerDirective;


  constructor(
    private fb: UntypedFormBuilder,
    private facilityService: FacilitiesService,
    private idnService: IdnService,
    private router: Router,
    private route: ActivatedRoute,
    private cultureService: CultureService,
    public toastrService: ToastrService,
    private resourcesService: ResourcesService
  ) { }

  ngOnInit(): void {

    this.isLoading = true;
    this.resourcesService.bsObject.subscribe(result => {
      result && (this.resourceKeyValue = result);
      this.resource = this.resourceKeyValue.resourceKeyFacility;
      this.setControlValues();
    });

    this.route.params.pipe(
      mergeMap((params) => {
        if (params['id']) {
          this.idFacility = parseInt(params['id'], 10);
          this.isCreate = false;
        } else {
          this.isCreate = true;
        }
        this.setControlValues();
        return this.facilityService.getAllCountry();
      }),
      mergeMap((response: any) => {
        this.allCountries = response;
        return this.cultureService.getAll$();
      }),
      mergeMap((response: any) => {
        this.allLanguages = response;
        return this.idnService.getAllIDNs();
      }),
      mergeMap((response: any) => {
        this.allIDNs = response.sort((a, b) => a.name.localeCompare(b.name, 'en', { caseFirst: 'upper' }));
        return this.facilityService.getFacilities();
      }),
      catchError(error => of([])),
      mergeMap((response: any) => {
        this.allHospitals = response;
        this.setFacilityValidations();
        if (this.idFacility > 0) {
          return this.facilityService.getFacilityById(this.idFacility);
        } else {
          this.showComboBox = true;
          return this.facilityService.getAllHybridIOCustomers();
        }
      })
    ).subscribe((response: any) => {
      if (this.idFacility > 0 && response && Object.keys(response).length > 0) {
        this.populateHospitalForm(response);
      } else {
        this.allIDNsCustomersFromRSS = response;
        this.setFieldValue('purgeFrequency', 30);
        this.isLoading = false;
      }

      this.setControlValues();
      this.isDataLoaded = true;
    });
  }

  setFacilityValidations(): void {
    this.hospitalForm.controls.selectFacilityName.setValidators(
      [
        Validators.required,
        Validators.maxLength(200),
        FacilityValidator.ValidateName(this.allHospitals),
        OnlySpacesValidator.onlyWhiteSpace
      ]);

    this.hospitalForm.controls.ods.setValidators([
      Validators.required,
      Validators.maxLength(500),
      FacilityValidator.ValidateODS(this.allHospitals),
      OnlySpacesValidator.onlyWhiteSpace
    ]);
  }

  onSubmit() {
    this.isLoading = true;
    var hospitalRequest = this.createRequestObject(this.hospitalForm);
    this.facilityService.createFacility(hospitalRequest).subscribe(response => {
      this.isLoading = false;
      var notificationMessage = "";
      var hasActiveUsersInFacility = response?.validationMessages?.find(x => x == 'HasActiveUsersInFacility');
      if (hasActiveUsersInFacility) {
        notificationMessage = this.resourceKeyValue.facilityErrorMessageActiveusersOnUpdate;
      } else if (response && response.cdsPublishFailed && this.isCreate) {
        notificationMessage = this.resourceKeyValue.facilityAndCdsSuccessMessageOnCreate;
      } else if (response && response.cdsPublishFailed && !this.isCreate) {
        notificationMessage = this.resourceKeyValue.facilityAndCdsSuccessMessageOnUpdate;
      }
      if (notificationMessage.length == 0) {
        if (this.isCreate) {
          notificationMessage = this.successMessageOnCreate;
        } else {
          notificationMessage = this.successMessageOnUpdate;
        }
      }
      if (hasActiveUsersInFacility) {
        this.showErrorNotification(notificationMessage);
      } else {
        this.showSuccessNotification(notificationMessage);
        if (response) {
          this.router.navigate(['/facilities']);
        } else {
          throw new Error('Failed to create Hospital');
        }
      }
    },
      (error) => {
        this.setControlValues();
        this.isLoading = false;
        console.error(error);
        if (error.code == 500) {
          if (this.isCreate) {
            this.showSuccessNotification(this.resourceKeyValue.facilityAndCdsSuccessMessageOnCreate);
          } else {
            this.showSuccessNotification(this.resourceKeyValue.facilityAndCdsSuccessMessageOnUpdate);
          }
        } else {
          if (this.isCreate) {
            this.showErrorNotification(this.errorMessageOnCreate);
          } else {
            this.showErrorNotification(this.errorMessageOnUpdate);
          }
        }
      });
  }

  createRequestObject(formGroup: UntypedFormGroup): IFacility {
    var countryId = formGroup.get('countryId')!.value;
    var localeId = this.allCountries.find(x => x.id = countryId).localeIdentifierId;
    var currentTenantCode = this.selectedIdnTenantCode;
    var previousTenatCode = this.previousIdnTenantCode;
    var currentFacilityRssId = this.facilityRssId;
    let selectedCustomer = this.allIDNsCustomersFromRSS && this.allIDNsCustomersFromRSS.length && this.allIDNsCustomersFromRSS.find((customer: ICustomerRss) => customer.customerName == formGroup.get('customerName')!.value)
    this.customerRssId = (selectedCustomer && selectedCustomer.customerId) || "";
    var currentClientRssId = this.customerRssId;
    var facilityName = formGroup.get('selectFacilityName')!.value;;
    var facilityId = this.idFacility;

    let requestObj = {
      name: facilityName,
      idnId: formGroup.get('idn')!.value,
      rssId: currentFacilityRssId,
      rssCustomerId: currentClientRssId,
      currentIdmIdnCode: currentTenantCode,
      previousIdmIdnCode: previousTenatCode,
      accountNumber: formGroup.get('accountNumber')!.value,
      address1: formGroup.get('address1')!.value,
      address2: formGroup.get('address2')!.value,
      city: formGroup.get('city')!.value,
      state: formGroup.get('state')!.value,
      zipcode: formGroup.get('zipCode')!.value,
      countryId: countryId,
      idn: {} as IIDN,
      id: facilityId,
      cultureId: localeId,
      originatingDataSource: formGroup.get('ods')!.value,
      previousOriginatingDataSource: this.currentOds ?? "",
      isCdsSubscribed: formGroup.get('isCdsSubscribed')!.value,
      cdsPublishFailed: false,
      purgeFrequency: formGroup.get('purgeFrequency')!.value,
      validationMessages: []
    };
    return requestObj;
  }

  populateHospitalForm(hospitalData: IFacility): void {

    this.currentIdn = this.allIDNs.find(x => x.id === hospitalData.idnId);
    this.selectedIDNValue = this.currentIdn?.id;
    this.selectedIdnTenantCode = this.currentIdn?.idmIdnCode;
    this.previousIdnTenantCode = this.currentIdn?.idmIdnCode;
    this.labelIdn = this.currentIdn?.name;
    this.currentOds = hospitalData.originatingDataSource;

    const countryId = this.allCountries?.find(i => i.id === hospitalData.countryId);
    //Select the country value in combobox
    this.selectedCountryId = countryId?.id || 0;

    //Get Faclity Rss Id
    this.facilityRssId = hospitalData.rssId;

    //Get Custormer Rss Id
    this.customerRssId = hospitalData.rssCustomerId;

    this.isEdit = false;
    this.setRssComboboxValues(this.customerRssId, this.facilityRssId);

    this.getAllStatesByCountryId(this.selectedCountryId, hospitalData);

    this.hospitalForm.controls.countryId.setValidators([]);

    this.selectedLanguageValue = hospitalData.cultureId;

    this.hospitalForm.patchValue({
      id: hospitalData?.id,
      selectFacilityName: hospitalData?.name,
      accountNumber: hospitalData?.accountNumber,
      address1: hospitalData?.address1,
      address2: hospitalData?.address2,
      city: hospitalData?.city,
      zipCode: hospitalData?.zipcode,
      cultureId: hospitalData?.cultureId,
      idn: hospitalData?.idnId,
      ods: hospitalData?.originatingDataSource,
      isCdsSubscribed: hospitalData?.isCdsSubscribed,
      purgeFrequency: hospitalData?.purgeFrequency
    });
  }

  setRssComboboxValues(customerRssId: string, facilityRssId: string) {
    this.facilityService.getAllHybridIOCustomers().pipe(
      mergeMap((response: any) => {
        this.allIDNsCustomersFromRSS = response;

        //Find existing customer by id (GUID)
        let existingCustomer = this.allIDNsCustomersFromRSS.find(x => x.customerId == customerRssId);
        this.customerRssId = existingCustomer?.customerId;

        if (this.customerRssId) {
          this.selectedCutomerValue = existingCustomer.customerName;

          return this.facilityService.getAllHybridIOFacilitiesByCustomerId(this.customerRssId)
        } else {
          return of([]);
        }
      })
    ).subscribe((response: any) => {
      this.customerRssId = "";

      if (response.length > 0) {

        this.allCustomerFacilities = response;
        //Find existing facility by id (GUID)
        let existingFacility = this.allCustomerFacilities.find(x => x.facilityId == facilityRssId);
        this.facilityRssId = existingFacility?.facilityId;

        if (this.facilityRssId) {
          this.setFieldValue('selectFacilityName', existingFacility.facilityName);
          this.selectedFacilityNameValue = existingFacility.facilityName;
        } else {
          this.facilityRssId = "";
        }
      } else {
        this.allCustomerFacilities = null;
        this.selectedFacilityNameValue = "0";
      }
      this.showComboBox = true;
      this.isLoading = false;
    });

  }

  setFieldValue(fieldName: string, fieldValue: any) {
    var val = this.hospitalForm.get(fieldName);
    if (val) {
      val.setValue(fieldValue);
    }
  }

  getAllStatesByCountryId(id: number, hospitalData?: any): any {
    this.facilityServiceSubscription = this.facilityService.getAllStatesByCountryId(id).subscribe(response => {
      if (response.length > 0) {
        this.allStates = response;
        this.showState = true;
        if (hospitalData && Object.keys(hospitalData).length) {
          const state = this.allStates?.find(i => i.code === hospitalData.state);
          this.setFieldValue('state', state.code);
        } else {
          this.setFieldValue('state', '0');
        }
      } else {
        this.showState = false;
        this.allStates = [{ id: 1, code: 'N/A', countryId: 1, name: 'N/A' }]
        this.setFieldValue('state', this.allStates[0]);
      }
    }, (error) => {
      this.setControlValues();
      console.error(error);
    }
    );
  }

  resetControlValues() {

    this.selectedCountryId = 0;
    this.selectedStateValue = '0';
    this.selectedIDNValue = 0;
    this.selectedCutomerValue = "0";
    this.customerRssId = "";
    this.facilityRssId = "";
    this.selectedFacilityNameValue = "0";
    this.selectedLanguageValue = 0;
    this.selectedIdnTenantCode = "";
    this.previousIdnTenantCode = "";

    this.setFieldValue('name', '');
    this.setFieldValue('accountNumber', '');
    this.setFieldValue('idn', 0);
    this.setFieldValue('countryId', 0);
    this.setFieldValue('address1', '');
    this.setFieldValue('address2', '');
    this.setFieldValue('city', '');
    this.setFieldValue('state', '0');
    this.setFieldValue('zipCode', '');
    this.setFieldValue('ods', '');
    this.setFieldValue('isCdsSubscribed', true);
    this.setFieldValue('purgeFrequency', 30);
    this.setControlsUntoched(this.hospitalForm);
  }
  setControlsUntoched(form: UntypedFormGroup) {
    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  showSuccessNotification(message: string) {
    this.toastrService.success(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  showErrorNotification(message: string) {
    this.toastrService.error(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  setControlValues() {

    if (this.isCreate) {
      this.lblCreateText = this.resourceKeyValue.createFacility;
    } else {
      this.lblCreateText = this.resourceKeyValue.editFacility;
    }

    this.btnCreateText = this.resourceKeyValue.save;
    this.btnCancelText = this.resourceKeyValue.cancel;

    this.successMessageOnCreate = this.resourceKeyValue.facilitySuccessMessageOnCreate;
    this.successMessageOnUpdate = this.resourceKeyValue.facilitySuccessMessageOnUpdate;
    this.errorMessageOnCreate = this.resourceKeyValue.facilityErrorMessageOnCreate;
    this.errorMessageOnUpdate = this.resourceKeyValue.facilityErrorMessageOnUpdate;
  }

  selectIDMIdnId(idxIDMName: number) {
    let existingIDM = this.allIDNs.find(x => x.id == idxIDMName)
    if (existingIDM != null) {
      this.selectedIDNValue = existingIDM.id;
      //Set tenant code
      this.selectedIdnTenantCode = existingIDM?.idmIdnCode
    } else {
      console.error("IDN from IDM not found in the current collection");
      this.selectedIDNValue = 0;
      this.selectedIdnTenantCode = "";
    }
  }

  //Get Customer Name from Customer Name combobox
  selectIDNCustomerName(idxIDNCustomerName: string) {
    this.isLoading = true;
    let existingCustomer = this.allIDNsCustomersFromRSS.find(x => x.customerName.toLowerCase() == idxIDNCustomerName.toLowerCase());
    this.customerRssId = existingCustomer?.customerId;

    this.clearFaciltyPopulatedFields();
    this.getAllFacilitiesByCustomerId(this.customerRssId);
  }

  clearFaciltyPopulatedFields() {
    this.setFieldValue('countryId', 0);
    this.setFieldValue('address1', '');
    this.setFieldValue('address2', '');
    this.setFieldValue('city', '');
    this.setFieldValue('state', '0');
    this.setFieldValue('zipCode', '');
  }

  getAllFacilitiesByCustomerId(id: string): any {
    this.facilityService.getAllHybridIOFacilitiesByCustomerId(id)
      .subscribe((response: any) => {
        this.isLoading = false;
        this.allCustomerFacilities = response.length > 0 ? response : null
        this.selectedFacilityNameValue = "0";
      }, (error) => {
        this.isLoading = false;
        this.setControlValues();
        console.error(error);
      });
  }

  //Get Facility Customer Name from Facility Name combobox
  selectFacilityName(idxFacilityName: string) {
    let existingFacility = this.allCustomerFacilities.find(x => x.facilityName.toLowerCase() == idxFacilityName.toLowerCase());
    this.hospitalForm.patchValue({
      id: -1,
      selectFacilityName: existingFacility?.facilityName,
      accountNumber: '',
      address1: existingFacility?.address1,
      address2: existingFacility?.address2,
      city: existingFacility?.cityName,
      zipCode: existingFacility?.zipCode
    });
    //Assign the RSS id Facility
    this.facilityRssId = existingFacility?.facilityId;
    if (existingFacility != null && existingFacility.countryName != null) {
      let country = this.allCountries.find(x => x.name.toLowerCase() == existingFacility.countryName.toLowerCase());
      this.selectedCountryId = country?.id;
      this.getAllStatesByCountryId(country?.id);
      if (existingFacility.stateName != null) {
        this.selectedStateValue = existingFacility.stateName;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.facilityServiceSubscription && !this.facilityServiceSubscription.closed) {
      this.facilityServiceSubscription.unsubscribe();
    }
  }
}